import { useEffect, useRef, useState, type ReactNode } from "react";

import { dataHora } from "../lib/format";
import { PILOTOS, type Piloto } from "../lib/pilotos";

interface Props {
  ativo: Piloto | null;
  /** `null` sai do modo piloto e volta ao vivo. */
  aoEscolher: (id: string | null) => void;
}

/**
 * Entrada e saída do modo piloto, no topo.
 *
 * Fechado é um botão discreto ao lado do "ao vivo"; aberto lista os pilotos do
 * registo com a janela de cada um, para se saber ao que se vai antes de clicar.
 * Ativo vira uma pastilha com o nome do piloto e um × — sair está sempre à
 * vista, porque um dashboard preso no passado sem saída óbvia parecia avariado.
 */
export function PilotoPicker({ ativo, aoEscolher }: Props): ReactNode {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement | null>(null);

  // Fecha ao clicar fora — o menu tapa o mapa e não pode ficar pendurado.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent): void => {
      if (raiz.current !== null && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberto]);

  if (ativo !== null) {
    return (
      <span className="piloto-pill">
        {ativo.rotulo}
        <button
          type="button"
          aria-label="Sair do piloto e voltar ao vivo"
          title="Voltar ao vivo"
          onClick={() => aoEscolher(null)}
        >
          ×
        </button>
      </span>
    );
  }

  return (
    <div className="piloto-picker" ref={raiz}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setAberto(false);
        }}
      >
        Piloto anterior
      </button>
      {aberto && (
        <div className="piloto-menu" role="menu">
          {PILOTOS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setAberto(false);
                aoEscolher(p.id);
              }}
            >
              <span>{p.rotulo}</span>
              <span className="janela">
                {dataHora(p.de)} — {dataHora(p.ate)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
